import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '@lmc-env/environment';
import { KapchargeTransactionFlow } from '@lmc-app/shared/enum/kapcharge-transaction-status.enum';
import { FundingType } from '@lmc-app/loan-servicing/payment-processing/funding-type.constant';
import { TransactionPreviewStatus } from '@lmc-app/shared/enum/transaction-preview-status.enum';

// ---------------------------------------------------------------------------
// KapCharge Response Code mapping
// ---------------------------------------------------------------------------

export const KAPCHARGE_RESPONSE_CODE_LABELS: Record<string, string> = {
  '900': 'Edit Reject',
  '901': 'NSF (Debit Only)',
  '902': 'Account not found',
  '903': 'Payment Stopped/Recalled',
  '904': 'Payment Stopped or Revoked',
  '905': 'Account Closed',
  '907': 'No Debit Allowed',
  '908': 'Funds Not Cleared (Debit Only)',
  '909': 'Currency/Account Mismatch',
  '910': 'Payor/Payee Deceased',
  '911': 'Account Frozen',
  '912': 'Invalid/Incorrect Account No.',
  '914': 'Incorrect Payor/Payee Name',
  '915': 'No Agreement Existed',
  '916': 'Not According to Agreement - Personal',
  '917': 'Agreement Revoked – Personal',
  '918': 'No Confirmation/Pre-Notification – Personal',
  '919': 'Not According to Agreement – Business',
  '920': 'Agreement Revoked – Business',
  '921': 'No Confirmation/Pre-Notification – Business',
  '922': 'Customer Initiated Return',
  '990': 'Institution in Default',
  '996': 'No Agreement',
};

export function getResponseCodeLabel(code: string | undefined | null): string | null {
  if (!code) return null;
  return KAPCHARGE_RESPONSE_CODE_LABELS[code] ?? null;
}

// ---------------------------------------------------------------------------
// Transaction Detail API types (KapCharge proxy)
// ---------------------------------------------------------------------------

export interface KapchargeTransactionEvent {
  eventDate: string;
  eventType: string;
  eventStatus?: string;
  description: string;
}

export interface KapchargeTransactionDetail {
  transactionStatus: string;
  merchantId: string;
  kapchargeTransactionId: number;
  clientReference: string;
  transactionMethod: string;
  transactionFlow: string;
  amountInCents: number;
  createdDateTime?: string;
  availableDate?: string;
  responseCode?: string;
  customer: {
    customerFullName: string;
    email: string;
  };
  transactionEvents: KapchargeTransactionEvent[];
}


// ---------------------------------------------------------------------------
// Local DB Batch types
// ---------------------------------------------------------------------------

/**
 * Per-status transaction counts. Shared by a batch and by each of its per-due-date
 * summaries so one mapping (kapchargeStatusItems) renders a breakdown for either.
 */
export interface KapchargeStatusCounts {
  queuedCount: number;
  initiatedCount: number;
  processingCount: number;
  acceptedCount: number;
  returnedCount: number;
  cancelledCount: number;
  invalidCount: number;
  /** Grouped count of Error + TxnError statuses. */
  errorCount: number;
}

/**
 * One due date covered by a batch, with the statuses and amount its transactions carry.
 * A batch can span several payment dates, so the batch detail dialog shows a breakdown
 * per entry rather than only the batch-wide totals.
 */
export interface KapchargeDueDateBreakdown extends KapchargeStatusCounts {
  /** Date-only (YYYY-MM-DD). */
  dueDate: string;
  totalTransaction: number;
  totalAmountInCents: number;
}

export interface KapchargeBatch extends KapchargeStatusCounts {
  batchId: string;
  originatorId: string;
  fundingType: string | null;
  fileType: string | null;
  /** Distinct due dates (YYYY-MM-DD, ascending) covered by the batch's transactions. */
  dueDates: string[];
  /** Same due dates as `dueDates`, each with its own counts and amount. */
  dueDateBreakdown: KapchargeDueDateBreakdown[];
  totalTransaction: number;
  totalAmountInCents: number;
  createdAt: string;
}

export interface KapchargeBatchResponse {
  results: KapchargeBatch[];
  currentPage: number;
  pageCount: number;
  pageSize: number;
  rowCount: number;
}

export interface KapchargeLocalTransaction {
  id: number;
  kapchargeTransactionId: number | null;
  amountInCents: number;
  dueDate: string | null;
  institutionCode: string | null;
  transitNumber: string | null;
  accountNumber: string | null;
  recipientName: string | null;
  loanNumber: string | null;
  customerName: string | null;
  // Batch-level metadata (originatorId, fileType)
  // lives on the parent KapchargeBatch, not on the item row.
  status: string | null;
  transactionFlow: string;
  /** True if a Pay-flow refund already exists for this transaction's nlsTransactionId. */
  alreadyRefunded: boolean;
  returnCode: string | null;
  returnedAt: string | null;
  /** Human-readable failure reason (validation failure or KapCharge submit error). */
  error: string | null;
  createdBy: number;
  createdAt: string;
}

/**
 * Whitelisted columns accepted by PATCH /payment-processing/transactions/:id. Mirrors
 * UPDATABLE_TRANSACTION_COLUMNS on the backend.
 */
export interface TransactionUpdatePayload {
  amountInCents?: number;
  dueDate?: string | null;
  institutionCode?: string | null;
  transitNumber?: string | null;
  accountNumber?: string | null;
  loanNumber?: string | null;
  status?: string;
  error?: string | null;
  /**
   * Optimistic-concurrency guard, not a column. When set, the backend only applies the
   * patch while the row is still in this status and answers 409 otherwise — the submit
   * cron claims Queued rows every minute, so an edit opened a moment earlier must not
   * land on a transaction already on its way to KapCharge.
   */
  expectedStatus?: string;
}

export interface KapchargeLocalTransactionResponse {
  results: KapchargeLocalTransaction[];
  currentPage: number;
  pageCount: number;
  pageSize: number;
  rowCount: number;
}

export interface RefundTransactionsResponse {
  batchId: string;
  count: number;
}

export interface KapchargeBatchSearchParams {
  page?: number;
  pageSize?: number;
  createdDateFrom?: string;
  createdDateTo?: string;
  /** Funding-type code ('DEB' | 'MFT' | 'SPV'); mapped to an exact originator ID on the backend. */
  fundingType?: string;
}

export interface KapchargeChildSearchParams {
  page?: number;
  pageSize?: number;
  transactionStatus?: string;
  loanNumber?: string;
  name?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  declinedDateFrom?: string;
  declinedDateTo?: string;
  returnCode?: string;
}

export interface KapchargeGlobalSearchParams {
  page?: number;
  pageSize?: number;
  /** Matched against the loan number OR the customer name; required by the endpoint. */
  search: string;
}

/**
 * A global-search hit: what the read-only result list renders, and nothing more.
 *
 * Deliberately NOT the full KapchargeLocalTransaction. The backend selects only these
 * columns because pulling the whole row drags in `error` (NVARCHAR(MAX)), and an
 * unbounded LOB in the select list gives the search query a plan that times out on a
 * selective term. Adding a field here means adding it to the endpoint's `attributes` —
 * check the query still returns before doing so, and never re-add `error`.
 *
 * `alreadyRefunded` is absent for the same read-only reason: no refund action here, so
 * the backend does not compute it. Every action stays on the batch page.
 */
export interface KapchargeSearchResult {
  id: number;
  loanNumber: string | null;
  /**
   * CIF "firstname middlename lastname" — consistently ordered, so this is what the list
   * shows. The middle name is part of it because the search matches on it.
   */
  customerName: string | null;
  /**
   * The loan account's own name: a separate string, sometimes surname-first, and the only
   * name a corporate borrower has. Not displayed as the customer's name, but it is one of
   * the fields the search matches on, so the UI surfaces it when it is why a row matched.
   */
  accountName: string | null;
  status: string | null;
  dueDate: string | null;
  amountInCents: number;
  /** The batch holding this transaction — the result row's way into the batch page. */
  batchId: string;
  fileType: string | null;
  /** Funding-type code ('DEB' | 'MFT' | 'SPV'), derived from the batch's originator. */
  fundingType: string | null;
  batchCreatedAt: string | null;
}

export interface KapchargeSearchResultResponse {
  results: KapchargeSearchResult[];
  currentPage: number;
  pageCount: number;
  pageSize: number;
  rowCount: number;
}

// ---------------------------------------------------------------------------
// Lookup API types
// ---------------------------------------------------------------------------

export interface PaymentTransaction {
  amount: number;
  dueDate: string;
  institutionCode: string;
  transitNumber: string;
  accountNumber: string;
  recipientName: string;
  branchName?: string | null;
  loanNumber: string | null;
  customerName: string | null;
  /**
   * Human-readable detail behind the row's status (e.g. "Missing customer info: Email,
   * Phone Number"), for display only — same role as the persisted
   * KapchargeTransaction.error column. Null when the row is Valid. Nothing branches on it.
   */
  error?: string | null;
  /**
   * Preview status — the single source of truth for how a row is handled. Set by the
   * backend lookup, changed only by a successful in-place fix (ManualMatch). Converted
   * to the persisted KapchargeTransactionStatus on submit.
   */
  status?: TransactionPreviewStatus;
}

export interface PaymentLookupResponse {
  transactions: PaymentTransaction[];
}

// ---------------------------------------------------------------------------
// Upload API types
// ---------------------------------------------------------------------------

export interface PaymentUploadResponse {
  parsedFile: {
    header: {
      customerNumber: string;
      fileType?: string;
      fileName?: string;
    };
    transactions: Array<{
      amount: number;
      dueDate: string;
      institutionCode: string;
      transitNumber: string;
      accountNumber: string;
      recipientName: string;
    }>;
  };
  lookupResults: PaymentTransaction[];
}

// ---------------------------------------------------------------------------
// Manual Transaction types
// ---------------------------------------------------------------------------

/** A bank account (loanacct_ach) held by the looked-up loan's customer. */
export interface LoanLookupBankAccount {
  institutionCode: string;
  transitNumber: string;
  accountNumber: string;
  /** Loan the ACH record belongs to — may be another loan of the same customer. */
  loanNumber: string | null;
  /** Raw loanacct_ach.status from NLS. */
  achStatus: number | null;
  /**
   * Outcome of the same validation the upload preview runs — `Valid`, or
   * `CustomerInfoIncomplete` when NLS is missing customer data KapCharge requires.
   */
  status: TransactionPreviewStatus;
  /** Detail behind the status (e.g. "Email, Phone Number"); null when Valid. */
  error?: string | null;
}

export interface LoanLookupResult {
  payeeName: string;
  /** Every bank account of the customer, regardless of ACH status; never empty. */
  bankAccounts: LoanLookupBankAccount[];
}

export interface ManualTransactionRow {
  loanNumber: string;
  payeeName: string;
  institutionCode: string;
  transitNumber: string;
  accountNumber: string;
  amount: number;
  paymentDate: string;
  /**
   * The promise to pay this payment settles, when the row came from one. The backend
   * links the promise to the created transaction and moves it to Submitted, in the same
   * transaction as the batch. Absent for a row typed into the dialog.
   */
  promiseToPayId?: number;
}

export interface ManualTransactionBatchPayload {
  /** Funding type ('DEB' | 'MFT' | 'SPV'); mapped to an originatorId on the backend. */
  fundingType: FundingType;
  /** Whole-batch flow: Collect (money-in) or Pay (money-out / manual refund). */
  transactionFlow: KapchargeTransactionFlow;
  transactions: ManualTransactionRow[];
}

export interface ManualTransactionRowError {
  index: number;
  field: string;
  message: string;
}

export interface ManualTransactionBatchResponse {
  batchId: string;
  count: number;
}

// ---------------------------------------------------------------------------
// Sanity Check types
// ---------------------------------------------------------------------------

/**
 * One line of a scheduled payment's breakdown — a non-ZZ row sharing the payment's
 * payment_reference_no (Principal and Interest, Principal Balloon, NSF Fee, ...).
 */
export interface SanityCheckPaymentComponent {
  paymentNumber: number;
  /** NLS payment_type code: PI, PB, F, L, U1, ... */
  paymentType: string;
  paymentDescription: string;
  amount: number;
  paid: number;
  remaining: number;
  transactionCode: number | null;
}

/**
 * One invalid payment found by the sanity check: the loan's latest scheduled payment
 * inside the selected window disagrees with its expected installment.
 */
export interface SanityCheckInvalidPayment {
  acctrefno: number;
  loanNumber: string;
  customerName: string;
  /** Groups the payment's rows in loanacct_payments_due; one reference is one payment. */
  paymentReferenceNo: string;
  /** date_due of the payment (YYYY-MM-DD). Every row of a reference shares it. */
  dueDate: string;
  /**
   * The loan's maturity as it stands today (loanacct.curr_maturity_date, YYYY-MM-DD), not
   * the original one it opened with. Null when NLS has none.
   */
  maturityDate: string | null;
  /** payment_amount of the payment's ZZ ("TOT PAYMENT") row — the amount actually charged. */
  scheduledAmount: number;
  /** amortized_payment_amount on loanacct_payment. */
  amortizedAmount: number;
  /** Recurring add-ons billed inside the ZZ total: insurance premium + tax. 0 when none. */
  recurringAmount: number;
  /** What the ZZ total should be: amortizedAmount + recurringAmount. */
  expectedAmount: number;
  /** The payment's non-ZZ rows. Usually, but not always, summing to scheduledAmount. */
  components: SanityCheckPaymentComponent[];
  // No `difference` field — it is derived for display (see SanityCheckDialogComponent).
}

/** One active loan of a customer who holds more than one. */
export interface SanityCheckCustomerLoan {
  acctrefno: number;
  /** The NLS customer record this loan sits under; a group can span more than one. */
  cifno: number;
  loanNumber: string;
  /**
   * True when this loan is one the selected date range found; false for a pulled-in sibling.
   * Not shown as a column — the due date says it — but it orders the loan list.
   */
  inDateRange: boolean;
  /**
   * Due date of the loan's next scheduled NLS payment on or after the range's first day
   * (YYYY-MM-DD). A sibling loan's normally falls outside the range. Null when the loan has
   * no remaining scheduled payment.
   */
  dueDate: string | null;
  /** payment_amount of that scheduled payment (the ZZ total). Null when there is none. */
  paymentAmount: number | null;
}

/**
 * One customer (NLS cifno) behind the checked date range who holds more than one active
 * loan. Their loans are listed in full — siblings are not limited to the date range.
 */
export interface SanityCheckMultiLoanCustomer {
  /** Stable id for the group (its lowest acctrefno) — customers can span several cifnos. */
  groupId: string;
  customerName: string | null;
  /** Every CIF record the group's loans sit under. More than one means NLS holds duplicates. */
  cifnos: number[];
  /**
   * Which rule tied these loans to one customer: "CIF", "Name + bank account", or both.
   * Loans are linked by a shared cifno OR by a shared name on a shared bank account.
   */
  linkedBy: string;
  loanCount: number;
  loans: SanityCheckCustomerLoan[];
}

export interface SanityCheckResponse {
  results: SanityCheckInvalidPayment[];
  rowCount: number;
  /** True when the backend result cap was hit and rows were left out. */
  truncated: boolean;
  multiLoanCustomers: SanityCheckMultiLoanCustomer[];
  multiLoanCustomerCount: number;
  /** True when the backend result cap was hit and customers were left out. */
  multiLoanTruncated: boolean;
  dateFrom: string;
  dateTo: string;
}

// ---------------------------------------------------------------------------
// Submit API types
// ---------------------------------------------------------------------------

@Injectable({
  providedIn: 'root',
})
export class PaymentProcessingService {
  private readonly uri: string = `${environment.DO_URL}/v2`;

  constructor(private readonly http: HttpClient) {}

  /**
   * Uploads an EFT (.txt) or ACH (.ach) file to the backend for parsing, storage, and loan lookup.
   * Returns parsed file metadata and lookup results for preview.
   */
  uploadFile(file: File, originatorType?: string): Observable<PaymentUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (originatorType) formData.append('originatorType', originatorType);
    return this.http.post<PaymentUploadResponse>(`${this.uri}/payment-processing/upload`, formData);
  }

  /**
   * Builds and downloads a CPA 005 EFT file from the given transaction ids.
   * All selected transactions must belong to the same batch.
   */
  exportEftFile(transactionIds: number[]): Observable<Blob> {
    return this.http.post(
      `${this.uri}/payment-processing/export-eft`,
      { transactionIds },
      { responseType: 'blob' }
    );
  }

  /**
   * Re-validates a single EFT transaction with corrected bank details.
   * Returns the matched transaction (loanNumber populated) or null if unmatched.
   */
  relookupPaymentTransaction(tx: PaymentTransaction): Observable<PaymentTransaction | null> {
    return this.http.post<PaymentLookupResponse>(
      `${this.uri}/payment-processing/lookup`,
      { transactions: [tx] }
    ).pipe(map((r) => r.transactions[0] ?? null));
  }

  /**
   * Fetches full transaction detail including transactionEvents timeline.
   */
  getTransaction(transactionId: number): Observable<KapchargeTransactionDetail> {
    return this.http.get<KapchargeTransactionDetail>(
      `${this.uri}/payment-processing/${transactionId}`
    );
  }

  /**
   * Fetches paged transaction batches with their aggregated item counts and amounts.
   */
  getTransactionGroups(params: KapchargeBatchSearchParams): Observable<KapchargeBatchResponse> {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });
    return this.http.get<KapchargeBatchResponse>(
      `${this.uri}/payment-processing/batches`,
      { params: httpParams }
    );
  }

  /**
   * Fetches one batch summary, in the same shape as an entry of getTransactionGroups.
   *
   * The batch detail page is a route, not a dialog handed its data — it loads the batch its
   * URL names, so the page survives a refresh or a pasted link. Answers 404 for an unknown
   * batchId.
   */
  getBatch(batchId: string): Observable<KapchargeBatch> {
    return this.http.get<KapchargeBatch>(
      `${this.uri}/payment-processing/batches/${encodeURIComponent(batchId)}`
    );
  }

  /**
   * Fetches all child transactions for a given batch from the local DB.
   */
  getTransactionsByBatchId(
    batchId: string,
    params: KapchargeChildSearchParams
  ): Observable<KapchargeLocalTransactionResponse> {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });
    return this.http.get<KapchargeLocalTransactionResponse>(
      `${this.uri}/payment-processing/batches/${encodeURIComponent(batchId)}/transactions`,
      { params: httpParams }
    );
  }

  /**
   * Searches transactions across every batch by loan number or customer name.
   *
   * The batch list can only filter batches, so this is how an operator holding a name or a
   * loan number — and no idea which batch holds the row — finds it. The customer name is
   * not stored on the transaction; the backend resolves it through the loan's NLS customer
   * and matches the term there.
   *
   * Rejects an empty term with 400, so callers must only call it with a non-blank search.
   */
  searchTransactions(params: KapchargeGlobalSearchParams): Observable<KapchargeSearchResultResponse> {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });
    return this.http.get<KapchargeSearchResultResponse>(
      `${this.uri}/payment-processing/transactions`,
      { params: httpParams }
    );
  }

  /**
   * Retries one or more failed transactions. The backend resets each to Queued so the
   * scheduled task re-sends them to KapCharge.
   *
   * `count` is how many rows the call reset; `transactions` carries the current state of
   * every requested row, so the caller can patch its table in place.
   */
  retryTransactions(
    transactionIds: number[]
  ): Observable<{ count: number; transactions: KapchargeLocalTransaction[] }> {
    return this.http.post<{ count: number; transactions: KapchargeLocalTransaction[] }>(
      `${this.uri}/payment-processing/transactions/retry`,
      { transactionIds }
    );
  }

  /**
  /**
   * General single-transaction update. Persists whichever whitelisted columns are
   * provided (e.g. { amountInCents, institutionCode, status }) and answers with the row as
   * it now stands, so the caller can patch its table in place.
   */
  updateTransaction(
    transactionId: number,
    patch: TransactionUpdatePayload
  ): Observable<{ id: number; transaction: KapchargeLocalTransaction }> {
    return this.http.patch<{ id: number; transaction: KapchargeLocalTransaction }>(
      `${this.uri}/payment-processing/transactions/${transactionId}`,
      patch
    );
  }

  /**
   * Creates a new "Pay"-flow batch that reverses the given (Processing or Accepted)
   * transactions. The source transactions are left untouched.
   */
  refundTransactions(transactionIds: number[]): Observable<RefundTransactionsResponse> {
    return this.http.post<RefundTransactionsResponse>(
      `${this.uri}/payment-processing/refund`,
      { transactionIds }
    );
  }

  /**
   * Cancels one or more transactions not yet sent to KapCharge (Queued, Invalid, or
   * Error). The backend flips each to Cancelled so the submit scheduled task skips them.
   *
   * `count` is how many rows the call changed; `transactions` carries the current state of
   * every requested row, so the caller can patch its table in place. A row the submit cron
   * claimed mid-call is not counted, and reads back as Initiated.
   */
  cancelTransactions(
    transactionIds: number[]
  ): Observable<{ count: number; transactions: KapchargeLocalTransaction[] }> {
    return this.http.post<{ count: number; transactions: KapchargeLocalTransaction[] }>(
      `${this.uri}/payment-processing/transactions/cancel`,
      { transactionIds }
    );
  }


  /**
   * Looks up payee name and bank details for a given loan number.
   */
  lookupLoan(loanNumber: string): Observable<LoanLookupResult> {
    return this.http.get<LoanLookupResult>(
      `${this.uri}/payment-processing/manual/loan-lookup/${encodeURIComponent(loanNumber)}`,
    );
  }

  /**
   * Creates a manual payment batch containing one or more transactions.
   * All rows share originator + paymentDate, written under a single batchId.
   * Backend returns { errors: [{ index, field, message }] } if any row fails.
   */
  createManualBatch(payload: ManualTransactionBatchPayload): Observable<ManualTransactionBatchResponse> {
    return this.http.post<ManualTransactionBatchResponse>(`${this.uri}/payment-processing/manual/submit`, payload);
  }

  /**
   * Runs the payment sanity check over a due-date window. Returns the loans whose latest
   * scheduled payment in that window does not match their amortized payment amount, plus
   * the customers behind those loans who hold more than one active loan.
   */
  runSanityCheck(dateFrom: string, dateTo: string): Observable<SanityCheckResponse> {
    const params = new HttpParams().set('dateFrom', dateFrom).set('dateTo', dateTo);
    return this.http.get<SanityCheckResponse>(
      `${this.uri}/payment-processing/sanity-check`,
      { params }
    );
  }

  /**
   * Submits uploaded payment transactions to the backend for DB persistence. Submission is
   * all-or-nothing — the preview only enables Submit once every row is queueable — so no
   * status is sent and the backend persists them all as `Queued`, ready for the scheduled
   * KapCharge submit task.
   */
  submitTransactions(
    transactions: PaymentTransaction[],
    originatorId?: string,
    fileType?: string,
    fileName?: string,
  ): Observable<void> {
    return this.http.post<void>(
      `${this.uri}/payment-processing/submit`,
      { transactions, originatorId, fileType, fileName }
    );
  }
}
